/**
 * FirstPartyDataPanel — shared "Drawing from your data room" panel used
 * in match-explanation surfaces (MatchExplainerModal, SkuRecommendations
 * modal, future wave-blueprint preview).
 *
 * Safe surface — the panel renders the brand's own data signals as
 * factual rows ("Top customer segments · 25-34 Female · Wellness").
 * No projected lift, no per-creator affinity claims, no scores. Those
 * are blocked until ML calibration lands (see CMD_PIPELINE_HEALTH_AUDIT).
 *
 * Renders nothing when the signals list is empty so consumers don't
 * have to branch — they just drop <FirstPartyDataPanel signals={...} />.
 *
 * Signal shape (provided by services.first_party_data_context):
 *   { label: string, note: string, source: string }
 */
import React from 'react';
import { Database, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ACCENT = '#A78BFA';     // violet — same accent as the data room page
const CARD   = '#0F0F12';

export default function FirstPartyDataPanel({ signals, className = '', testId = 'first-party-panel' }) {
  if (!signals || signals.length === 0) return null;

  return (
    <div
      data-testid={testId}
      className={`rounded-xl p-3.5 ${className}`}
      style={{ background: CARD, border: `1px solid ${ACCENT}33` }}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5">
          <Database className="h-3 w-3" style={{ color: ACCENT }} />
          <p className="text-[9px] uppercase tracking-[0.18em] font-black" style={{ color: ACCENT }}>
            Drawing from your data room
          </p>
        </div>
        <Link
          to="/data-room"
          data-testid={`${testId}-open-link`}
          className="text-[9px] font-bold text-gray-500 hover:text-white transition-colors flex items-center gap-0.5"
        >
          Open <ChevronRight className="h-2.5 w-2.5" />
        </Link>
      </div>

      <ul className="space-y-1.5">
        {signals.map(sig => (
          <li
            key={sig.source}
            data-testid={`${testId}-row-${sig.source}`}
            className="flex items-start gap-2"
          >
            <span
              className="mt-1 w-1 h-1 rounded-full flex-shrink-0"
              style={{ background: ACCENT }}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-white">{sig.label}</p>
              <p className="text-[10px] text-gray-500 leading-snug truncate">{sig.note}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
